import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Snackbar, 
  Alert, 
  Typography,
  Paper,
  IconButton
} from '@mui/material';
import { 
  GetApp as InstallIcon,
  Close as CloseIcon
} from '@mui/icons-material';

/**
 * @component PWAInstallPrompt
 * @description Component that prompts users to install the Laprecio PWA
 */
const PWAInstallPrompt = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallSuccess, setShowInstallSuccess] = useState(false);

  useEffect(() => {
    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      // App is already installed, don't show the prompt
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Update UI to show the install button
      setIsInstallable(true);
      // Show the banner after a short delay
      setTimeout(() => {
        setShowBanner(true);
      }, 3000);
    };

    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      // Hide the banner
      setShowBanner(false);
      // Show success message
      setShowInstallSuccess(true);
      // Log the installation to analytics
      console.log('PWA was installed');
      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setIsInstallable(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    // Log the outcome
    console.log(`User response to the install prompt: ${outcome}`);
    
    // If the user accepted, hide the banner
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    
    // We've used the prompt, and can't use it again, discard it
    setDeferredPrompt(null);
  };

  const handleCloseBanner = () => {
    setShowBanner(false);
    // Don't show the banner again for this session
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  const handleCloseSuccess = () => {
    setShowInstallSuccess(false);
  };

  // Don't render anything if the app is not installable
  if (!isInstallable) {
    return null;
  }

  return (
    <>
      {/* Install Banner */}
      {showBanner && (
        <Paper
          id="pwa-install-banner"
          elevation={3}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            zIndex: 9999,
            p: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'primary.light',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1, pr: 2 }}>
              <Typography variant="h6" gutterBottom>
                Install Laprecio App
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Install our app for a better experience with offline access, faster loading, and easy access from your home screen.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<InstallIcon />}
                onClick={handleInstallClick}
                sx={{ borderRadius: 50 }}
              >
                Install Now
              </Button>
            </Box>
            <IconButton
              size="small"
              aria-label="close"
              onClick={handleCloseBanner}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showInstallSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Laprecio app was successfully installed!
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt;
